import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase, Paper, Popper, Typography } from '@mui/material';
import { color, r, radius, shadow } from '../../theme/tokens';
import { ADD_ANOTHER, AT_CAP, AT_CAP_SIGNED_IN } from './copy';

// One of v5.1's two fields (STACK-587, states 1–6): a heading, a deep-green
// instruction line, and a hairline box holding the picks as chips with ×, then
// the search input. Matches open below it in an MUI Popper. At the cap the box
// stops taking input and says so underneath.
//
// v5.1's colours that are not tokens map to the nearest token (the PR lists
// the values): the chips' sage border and remove circle → `inkSoft`, the
// focus ring → `borderSoft`, the highlighted match → `mintSoft`.

/** The id the field's input points at; the caller's listbox carries it. */
export const listboxIdFor = (id) => `${id}-listbox`;

export const fieldHeadingSx = {
  m: 0,
  fontWeight: 800,
  fontSize: r(20),
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: color.ink,
};

const Chip = ({ label, onRemove }) => (
  <Box
    component="li"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: r(36),
      pl: r(12),
      pr: r(2),
      border: `${r(1)} solid ${color.inkSoft}`,
      borderRadius: radius.sm,
      backgroundColor: color.paper,
      color: color.evergreen,
      fontSize: r(14),
      lineHeight: 1.3,
      maxWidth: '100%',
    }}
  >
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
    <ButtonBase
      onClick={onRemove}
      aria-label={`Remove ${label}`}
      sx={{ flex: 'none', width: r(36), height: r(36), borderRadius: radius.sm }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: r(18),
          height: r(18),
          borderRadius: '50%',
          backgroundColor: color.inkSoft,
          color: color.paper,
          display: 'grid',
          placeItems: 'center',
          fontSize: r(12),
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ×
      </Box>
    </ButtonBase>
  </Box>
);

/**
 * @param {Object} props
 * @param {string} props.id - prefix for the field's element ids
 * @param {string} props.heading
 * @param {string} props.instruction
 * @param {string} props.placeholder - shown while nothing is picked
 * @param {Array<{id: *, label: string}>} props.chips
 * @param {Function} props.onRemove - `(id) => void`
 * @param {boolean} props.atCap
 * @param {boolean} [props.signedIn] - picks the cap line without "free"
 * @param {string} props.term
 * @param {Function} props.onTermChange
 * @param {Function} props.onKeyDown
 * @param {boolean} props.dropdownOpen
 * @param {React.ReactNode} props.dropdown - the matches, or a no-match / error state
 * @param {string} [props.activeOptionId] - the highlighted option's id
 */
export default function PickField({
  id,
  heading,
  instruction,
  placeholder,
  chips,
  onRemove,
  atCap,
  signedIn = false,
  term,
  onTermChange,
  onKeyDown,
  dropdownOpen,
  dropdown,
  activeOptionId,
}) {
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [width, setWidth] = useState(0);
  const listboxId = listboxIdFor(id);
  const open = Boolean(dropdownOpen && focused && !atCap && boxRef.current);

  // The Popper matches the field's width, re-measured whenever it opens.
  useEffect(() => {
    if (open && boxRef.current) setWidth(boxRef.current.offsetWidth);
  }, [open]);

  // At the cap the input goes away. If it had focus, focus moves to the last
  // pick's remove button rather than falling to the page.
  useEffect(() => {
    if (!atCap || !focused) return;
    setFocused(false);
    const buttons = boxRef.current?.querySelectorAll('button');
    buttons?.[buttons.length - 1]?.focus();
  }, [atCap, focused]);

  const hasChips = chips.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: r(8) }}>
      <Typography component="h2" id={`${id}-heading`} sx={fieldHeadingSx}>{heading}</Typography>
      <Typography component="p" id={`${id}-instruction`} sx={{ m: 0, fontSize: r(14), fontWeight: 600, color: color.evergreen }}>
        {instruction}
      </Typography>
      <Box
        ref={boxRef}
        onClick={() => inputRef.current?.focus()}
        sx={{
          minHeight: r(52),
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: r(6),
          px: hasChips ? r(8) : r(14),
          py: hasChips ? r(7) : 0,
          border: focused && !atCap ? `${r(1.5)} solid ${color.accent}` : `${r(1)} solid ${color.line}`,
          borderRadius: radius.md,
          boxShadow: focused && !atCap ? `0 0 0 ${r(4)} ${color.borderSoft}` : 'none',
          backgroundColor: color.paper,
          cursor: atCap ? 'default' : 'text',
        }}
      >
        {hasChips && (
          <Box component="ul" aria-label={heading} sx={{ display: 'contents', listStyle: 'none', m: 0, p: 0 }}>
            {chips.map((chip) => (
              <Chip key={chip.id} label={chip.label} onRemove={() => onRemove(chip.id)} />
            ))}
          </Box>
        )}
        {!atCap && (
          <InputBase
            inputRef={inputRef}
            value={term}
            onChange={(e) => onTermChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={hasChips ? ADD_ANOTHER : placeholder}
            inputProps={{
              role: 'combobox',
              'aria-expanded': open,
              'aria-controls': open ? listboxId : undefined,
              'aria-activedescendant': open ? activeOptionId : undefined,
              'aria-autocomplete': 'list',
              'aria-labelledby': `${id}-heading`,
              'aria-describedby': `${id}-instruction`,
              autoComplete: 'off',
              autoCorrect: 'off',
              spellCheck: 'false',
            }}
            // 16px floor: iOS zooms the page when focusing a smaller input.
            sx={{
              flex: 1,
              minWidth: r(80),
              pl: hasChips ? r(6) : 0,
              fontSize: '1rem',
              color: color.ink,
              '& input::placeholder': { color: color.inkMuted, opacity: 1 },
            }}
          />
        )}
      </Box>
      {atCap && (
        <Typography role="status" sx={{ m: 0, fontSize: r(13), lineHeight: 1.45, color: color.inkSoft }}>
          {signedIn ? AT_CAP_SIGNED_IN : AT_CAP}
        </Typography>
      )}
      <Popper
        open={open}
        anchorEl={boxRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }, { name: 'flip', enabled: false }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper
          // Keeps focus in the input, so a click picks before blur closes it.
          onMouseDown={(e) => e.preventDefault()}
          elevation={0}
          sx={{
            width: width ? r(width) : 'auto',
            maxWidth: 'calc(100vw - 2rem)',
            backgroundColor: color.paper,
            border: `${r(1)} solid ${color.line}`,
            borderRadius: radius.md,
            boxShadow: shadow.pop,
            overflow: 'hidden',
          }}
        >
          {dropdown}
        </Paper>
      </Popper>
    </Box>
  );
}

/** A match row inside the dropdown. */
export const OptionRow = ({ id, selected, onSelect, onHover, children, minHeight = 52 }) => (
  <Box
    component="li"
    id={id}
    role="option"
    aria-selected={selected}
    onClick={onSelect}
    onMouseEnter={onHover}
    sx={{
      px: r(14),
      py: r(10),
      minHeight: r(minHeight),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      cursor: 'pointer',
      backgroundColor: selected ? color.mintSoft : 'transparent',
      borderBottom: `${r(1)} solid ${color.lineSoft}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    {children}
  </Box>
);

/** The matched part of a name in bold, as v5.1 draws it. */
export const Highlight = ({ text, term }) => {
  const i = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <Box component="b" sx={{ fontWeight: 700 }}>{text.slice(i, i + term.length)}</Box>
      {text.slice(i + term.length)}
    </>
  );
};

/** A message in the dropdown: no match, search failed. */
export const DropdownMessage = ({ title, children, role = 'status' }) => (
  <Box role={role} sx={{ p: r(14), display: 'flex', flexDirection: 'column', gap: r(4) }}>
    {title && <Box component="b" sx={{ fontSize: r(14), fontWeight: 700, color: color.ink }}>{title}</Box>}
    <Box component="span" sx={{ fontSize: r(14), lineHeight: 1.45, color: color.textBody }}>{children}</Box>
  </Box>
);
